import { useState, useRef, useEffect } from 'react';
import { MoreVertical, Send, XCircle, Eye, CheckCircle } from 'lucide-react';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { timeAgo } from '@dashboard/shared/timeAgo';
import type { Invoice, InvoiceStatus } from './useStripeInvoices';

interface Props {
  invoices: Invoice[];
  onSelect: (invoice: Invoice) => void;
  onUpdateStatus: (id: string, status: InvoiceStatus) => void;
}

function isOverdue(inv: Invoice) {
  return inv.status === 'sent' && inv.due_date && new Date(inv.due_date) < new Date();
}

function ActionMenu({
  invoice,
  onSelect,
  onUpdateStatus,
}: {
  invoice: Invoice;
  onSelect: (inv: Invoice) => void;
  onUpdateStatus: (id: string, status: InvoiceStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredTrigger, setHoveredTrigger] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1 rounded transition-colors"
        style={{
          color: hoveredTrigger ? 'var(--dash-text-tertiary)' : 'var(--dash-text-dim)',
          background: hoveredTrigger ? 'var(--dash-hover-bg)' : 'transparent',
        }}
        onMouseEnter={() => setHoveredTrigger(true)}
        onMouseLeave={() => setHoveredTrigger(false)}
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 w-44 rounded shadow-xl py-1"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <button
            onClick={() => { onSelect(invoice); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-sans transition-colors"
            style={{
              color: 'var(--dash-text-tertiary)',
              background: hoveredItem === 'view' ? 'var(--dash-hover-bg)' : 'transparent',
            }}
            onMouseEnter={() => setHoveredItem('view')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <Eye size={12} /> View Details
          </button>
          {invoice.status === 'draft' && (
            <>
              <div style={{ borderTop: '1px solid var(--dash-border)', margin: '4px 0' }} />
              <button
                onClick={() => { onUpdateStatus(invoice.id, 'sent'); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-sans text-blue-400 transition-colors"
                style={{
                  background: hoveredItem === 'send' ? 'var(--dash-hover-bg)' : 'transparent',
                }}
                onMouseEnter={() => setHoveredItem('send')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <Send size={12} /> Send
              </button>
              <button
                onClick={() => { onUpdateStatus(invoice.id, 'cancelled'); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-sans transition-colors"
                style={{
                  color: 'var(--dash-text-dim)',
                  background: hoveredItem === 'cancel' ? 'var(--dash-hover-bg)' : 'transparent',
                }}
                onMouseEnter={() => setHoveredItem('cancel')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <XCircle size={12} /> Cancel
              </button>
            </>
          )}
          {(invoice.status === 'sent' || isOverdue(invoice)) && (
            <>
              <div style={{ borderTop: '1px solid var(--dash-border)', margin: '4px 0' }} />
              <button
                onClick={() => { onUpdateStatus(invoice.id, 'paid'); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-sans text-emerald-400 transition-colors"
                style={{
                  background: hoveredItem === 'paid' ? 'var(--dash-hover-bg)' : 'transparent',
                }}
                onMouseEnter={() => setHoveredItem('paid')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <CheckCircle size={12} /> Mark Paid
              </button>
              <button
                onClick={() => { onUpdateStatus(invoice.id, 'cancelled'); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-sans text-red-400 transition-colors"
                style={{
                  background: hoveredItem === 'cancel-sent' ? 'var(--dash-hover-bg)' : 'transparent',
                }}
                onMouseEnter={() => setHoveredItem('cancel-sent')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <XCircle size={12} /> Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function InvoiceTable({ invoices, onSelect, onUpdateStatus }: Props) {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  return (
    <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
      <table className="w-full text-sm font-sans">
        <thead>
          <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Invoice
            </th>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Recipient
            </th>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Amount
            </th>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Due
            </th>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Status
            </th>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Created
            </th>
            <th
              className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium w-10"
              style={{ color: 'var(--dash-text-dim)' }}
            ></th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv, i) => (
            <tr
              key={inv.id}
              className="transition-colors cursor-pointer last:border-b-0"
              style={{
                background: isOverdue(inv)
                  ? 'rgba(239,68,68,0.03)'
                  : hoveredRow === inv.id
                    ? 'var(--dash-hover-bg)'
                    : i % 2 === 0
                      ? 'var(--dash-row-even)'
                      : 'var(--dash-row-odd)',
                borderBottom: '1px solid var(--dash-border-light)',
              }}
              onClick={() => onSelect(inv)}
              onMouseEnter={() => setHoveredRow(inv.id)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <td className="px-4 py-2.5">
                <div className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                  {inv.number}
                </div>
                {inv.description && (
                  <div
                    className="font-sans text-[10px] mt-0.5 truncate max-w-[140px]"
                    title={inv.description}
                    style={{ color: 'var(--dash-text-faint)' }}
                  >
                    {inv.description}
                  </div>
                )}
              </td>
              <td
                className="px-4 py-2.5 font-sans text-xs"
                style={{ color: 'var(--dash-text-muted)' }}
              >
                {inv.recipient}
              </td>
              <td
                className="px-4 py-2.5 font-mono text-xs"
                style={{ color: 'var(--dash-text-tertiary)' }}
              >
                ${Number(inv.amount).toFixed(2)}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs">
                {inv.due_date ? (
                  <span className={isOverdue(inv) ? 'text-red-400' : ''}
                    style={isOverdue(inv) ? undefined : { color: 'var(--dash-text-dim)' }}
                  >
                    {inv.due_date}
                  </span>
                ) : (
                  <span style={{ color: 'var(--dash-text-faint)' }}>--</span>
                )}
              </td>
              <td className="px-4 py-2.5">
                <InvoiceStatusBadge status={inv.status} dueDate={inv.due_date} />
              </td>
              <td
                className="px-4 py-2.5 font-mono text-xs"
                style={{ color: 'var(--dash-text-dim)' }}
              >
                {timeAgo(inv.created_at)}
              </td>
              <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                <ActionMenu
                  invoice={inv}
                  onSelect={onSelect}
                  onUpdateStatus={onUpdateStatus}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
